import { useEffect, useMemo, useState } from 'react';
import Spreadsheet from '../components/Spreadsheet';
import {
  buildFiniquitosDocumentSnapshot,
  calculateFiniquito,
  DEFAULT_VACATION_TABLE,
  DEFAULT_VACATION_TABLE_VERSION
} from '../lib/finiquitos/calc';
import { formatCurrency } from '../utils/formatters';
import {
  buildVacationConfigSnapshot,
  createBenefitRowFromPreset,
  createConceptRowFromPreset,
  createEmptyBenefitRow,
  createEmptyConceptRow,
  createVacationSpreadsheetData,
  FINIQUITOS_BENEFIT_TYPE_OPTIONS,
  FINIQUITOS_CONCEPT_PRESETS,
  FINIQUITOS_DEFAULT_EMPLOYEE,
  FINIQUITOS_DEFAULT_SEPARATION,
  FINIQUITOS_REASON_OPTIONS,
  FINIQUITOS_VACATION_TABLE_MODE_OPTIONS,
  FINIQUITOS_VACATION_TABLE_MODES,
  normalizeConceptRows,
  normalizeDraftNumber,
  normalizeDraftText,
  normalizeVacationTableMode,
  spreadsheetDataToVacationRows,
  validateVacationRows
} from '../utils/finiquitosForm';
import { useFiniquitosEmployers } from '../hooks/useFiniquitosEmployers';
import {
  checkFiniquitosBackendHealth,
  downloadFiniquitosDocument,
  getFiniquitosApiConfigError,
  submitFiniquitoDocument,
  submitFiniquitosBundle
} from '../services/finiquitosApi';

const FINIQUITOS_DOCUMENT_TEMPLATE_VERSION =
  process.env.REACT_APP_FINIQUITOS_DOCUMENT_TEMPLATE_VERSION || 'finiquitos-docx.v1';

const FINIQUITOS_GENERATION_STATUS_META = {
  pending: { label: 'Pendiente', className: 'warning' },
  generating: { label: 'Generando documentos', className: 'warning' },
  generated: { label: 'Documentos listos', className: 'ready' },
  business_error: { label: 'Error de negocio', className: 'danger' },
  technical_error: { label: 'Error técnico', className: 'danger' },
  backend_unconfigured: { label: 'Backend no configurado', className: 'warning' },
  error: { label: 'Error', className: 'danger' },
  idle: { label: 'Pendiente', className: 'neutral' }
};

const FINIQUITOS_BACKEND_STATUS_META = {
  checking: { label: 'Verificando backend', className: 'warning' },
  ready: { label: 'Backend disponible', className: 'ready' },
  unconfigured: { label: 'Backend no configurado', className: 'warning' },
  unavailable: { label: 'Backend no disponible', className: 'danger' },
  unhealthy: { label: 'Health fallido', className: 'danger' }
};

function getGenerationStatusMeta(status) {
  return FINIQUITOS_GENERATION_STATUS_META[status] || FINIQUITOS_GENERATION_STATUS_META.pending;
}

function getBackendStatusMeta(status) {
  return FINIQUITOS_BACKEND_STATUS_META[status] || FINIQUITOS_BACKEND_STATUS_META.checking;
}

function buildRunId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `calc-${crypto.randomUUID()}`;
  }
  return `calc-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function formatCount(value) {
  return Number(value || 0).toLocaleString('es-MX');
}

function displayIsoDate(value) {
  const text = String(value || '').trim();
  return text || 'Sin fecha';
}

function isBlockingBackendStatus(status) {
  return ['unconfigured', 'unavailable', 'unhealthy'].includes(status);
}

function normalizeDocumentFieldValue(value) {
  return String(value ?? '').trim();
}

function getMissingDocumentFields(documentSnapshot = {}) {
  const missingFields = [];
  const employer = documentSnapshot?.employer || {};
  const dates = documentSnapshot?.dates || {};

  if (!normalizeDocumentFieldValue(employer.name)) {
    missingFields.push('nombre del empleador');
  }
  if (!normalizeDocumentFieldValue(employer.rfc)) {
    missingFields.push('RFC del empleador');
  }
  if (!normalizeDocumentFieldValue(employer.registroPatronal)) {
    missingFields.push('registro patronal');
  }
  if (!normalizeDocumentFieldValue(dates.ciudadFirma)) {
    missingFields.push('ciudad de firma');
  }
  if (!normalizeDocumentFieldValue(dates.estadoFirma)) {
    missingFields.push('estado de firma');
  }

  return missingFields;
}

function mapBackendErrorToSubmitStatus(error) {
  if (error?.kind === 'business') {
    return 'business_error';
  }
  if (error?.kind === 'config') {
    return 'backend_unconfigured';
  }
  return 'technical_error';
}

const EMPLOYER_SEARCH_FIELDS = [
  { value: 'all', label: 'Todo' },
  { value: 'label', label: 'Empresa' },
  { value: 'rfc', label: 'RFC' },
  { value: 'patronal', label: 'Registro patronal' },
  { value: 'code', label: 'ID cliente' }
];

function makeEmployerPayload(employer) {
  if (!employer) return null;
  const registroPatronal =
    employer.registroPatronal || employer.patronal || employer.reg_patronal || '';
  return {
    id: employer.id,
    label: employer.label,
    name: employer.company || employer.label,
    company: employer.company,
    rfc: employer.rfc,
    patronal: registroPatronal,
    registroPatronal,
    code: employer.code,
    airtableId: employer.airtableId || employer.id
  };
}

function isRenunciaVoluntaria(value) {
  return value === 'renuncia_voluntaria';
}

function normalizeConceptLabel(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function isManualIsrConcept(concept) {
  if (!concept || concept.category !== 'deduccion') return false;
  const normalizedLabel = normalizeConceptLabel(concept.label);
  return normalizedLabel === 'isr' || normalizedLabel === 'isr manual' || normalizedLabel === 'isr retenido';
}

function formatDocumentStorage(document) {
  const storage = document?.storage || {};
  return [storage.provider, storage.id, storage.path].filter(Boolean).join(' · ') || 'Sin ubicación';
}

function buildLegacyFiniquitosPayload({ validation, session, calculationRunId, documentSnapshot, documentMode, documentRequests }) {
  const normalizeLegacyPerception = row => {
    const concept = String(row?.concepto || '').trim();
    const amount = Number(row?.importe || 0);
    const lower = concept.toLowerCase();
    if (lower.includes('aguinaldo')) {
      return { clave: '23', concepto: 'Aguinaldo (proporcional)', importe: amount };
    }
    if (lower.includes('vacaciones') && lower.includes('no pag')) {
      return { clave: '21', concepto: 'Vacaciones (No Pagadas)', importe: amount };
    }
    if (lower.includes('vacaciones')) {
      return { clave: '21', concepto: 'Vacaciones (proporcional)', importe: amount };
    }
    if (lower.includes('prima vacacional') && lower.includes('no pag')) {
      return { clave: '22', concepto: 'P. Vac. (No Pagada)', importe: amount };
    }
    if (lower.includes('prima vacacional')) {
      return { clave: '22', concepto: 'Prima Vacacional (proporcional)', importe: amount };
    }
    return {
      clave: String(row?.clave || ''),
      concepto: concept,
      importe: amount
    };
  };
  const normalizeLegacyDeduction = row => {
    const concept = String(row?.concepto || '').trim();
    const amount = Number(row?.importe || 0);
    const lower = concept.toLowerCase();
    if (lower.includes('isr')) {
      return { clave: 'ISR', concepto: 'ISR retenido', importe: amount };
    }
    return {
      clave: String(row?.clave || 'OTR'),
      concepto: concept || 'Otros descuentos',
      importe: amount
    };
  };
  const legacyEmployee = {
    nombreCompleto: documentSnapshot?.employee?.fullName || '',
    nombre: documentSnapshot?.employee?.fullName || '',
    curp: documentSnapshot?.employee?.curp || '',
    puesto: validation?.separation?.puesto || validation?.employee?.puesto || '',
    rfc: validation?.employee?.rfc || '',
    nss: validation?.employee?.nss || '',
    correo: validation?.employee?.correo || '',
    telefono: validation?.employee?.telefono || ''
  };
  const legacyEmployer = {
    nombre: documentSnapshot?.employer?.name || validation?.employer?.name || validation?.employer?.company || validation?.employer?.label || '',
    rfc: documentSnapshot?.employer?.rfc || validation?.employer?.rfc || '',
    registroPatronal:
      documentSnapshot?.employer?.registroPatronal ||
      validation?.employer?.registroPatronal ||
      validation?.employer?.patronal ||
      ''
  };
  const legacyPerceptions = Array.isArray(documentSnapshot?.percepciones)
    ? documentSnapshot.percepciones.map(normalizeLegacyPerception)
    : [];
  const legacyDeductions = Array.isArray(documentSnapshot?.deducciones)
    ? documentSnapshot.deducciones
        .map(normalizeLegacyDeduction)
        .sort((left, right) => {
          const leftIsr = String(left.concepto || '').toLowerCase().includes('isr') ? 0 : 1;
          const rightIsr = String(right.concepto || '').toLowerCase().includes('isr') ? 0 : 1;
          return leftIsr - rightIsr;
        })
    : [];
  const legacyMovements = Array.isArray(validation?.calculation?.lineItems)
    ? validation.calculation.lineItems.map(item => ({
        kind: item.direction === 'debit' ? 'deduccion' : 'percepcion',
        tipo: item.direction === 'debit' ? 'Deducción' : 'Percepción',
        concepto: item.label || '',
        importe: Number(item.amount || 0)
      }))
    : [];
  const renunciaVoluntaria = Boolean(validation?.renunciaVoluntaria);

  return {
    calculation_run_id: calculationRunId,
    calculationRunId,
    document_mode: documentMode,
    documentMode,
    document_requests: documentRequests,
    documentRequests,
    requested_documents: documentRequests,
    requestedDocuments: documentRequests,
    bundleRenuncia: documentMode === 'bundle',
    generarRenuncia: documentMode === 'bundle',
    renuncia: documentMode === 'bundle',
    renunciaVoluntariaConfirmada: renunciaVoluntaria,
    renuncia_voluntaria_confirmada: renunciaVoluntaria,
    tipo_baja: validation?.separation?.motivo || '',
    tipoBaja: validation?.separation?.motivo || '',
    tipo_baja_label: validation?.separation?.motivo || '',
    tipoBajaLabel: validation?.separation?.motivo || '',
    fecha_baja: validation?.separation?.fechaSeparacion || '',
    fechaBaja: validation?.separation?.fechaSeparacion || '',
    fechaSalida: validation?.separation?.fechaSeparacion || '',
    fecha: validation?.separation?.fechaSeparacion || '',
    fechaCalculo: validation?.separation?.fechaCalculo || '',
    fechaIngreso: validation?.separation?.fechaIngreso || '',
    fechaAlta: validation?.separation?.fechaAlta || '',
    fechaFirma: validation?.separation?.fechaFirma || '',
    ciudadFirma: validation?.separation?.ciudadFirma || '',
    estadoFirma: validation?.separation?.estadoFirma || '',
    aguinaldo_dias_anuales: validation?.separation?.aguinaldoDiasAnuales ?? '',
    aguinaldoDiasAnuales: validation?.separation?.aguinaldoDiasAnuales ?? '',
    aguinaldo_dias_laborados: validation?.separation?.aguinaldoDiasLaborados ?? '',
    aguinaldoDiasLaborados: validation?.separation?.aguinaldoDiasLaborados ?? '',
    dias_indemnizacion: validation?.separation?.diasIndemnizacion ?? '',
    diasIndemnizacion: validation?.separation?.diasIndemnizacion ?? '',
    vacaciones_no_pagadas_dias: validation?.separation?.vacacionesNoPagadasDias ?? '',
    vacacionesNoPagadasDias: validation?.separation?.vacacionesNoPagadasDias ?? '',
    empleadoNombre: legacyEmployee.nombre,
    nombreEmpleado: legacyEmployee.nombre,
    nombre: legacyEmployee.nombre,
    empleado: legacyEmployee,
    empresaNombre: legacyEmployer.nombre,
    empresa: legacyEmployer,
    puesto: legacyEmployee.puesto,
    percepciones: legacyPerceptions,
    deducciones: legacyDeductions,
    movimientos: legacyMovements,
    total_percepciones: Number(documentSnapshot?.totals?.totalPercepciones || 0),
    total_deducciones: Number(documentSnapshot?.totals?.totalDeducciones || 0),
    neto: Number(documentSnapshot?.totals?.netoPagar || 0),
    netoPagar: Number(documentSnapshot?.totals?.netoPagar || 0),
    documentSnapshot,
    snapshot: documentSnapshot,
    approved_snapshot: documentSnapshot,
    approvedSnapshot: documentSnapshot,
    audit_user_id: session?.uid || '',
    auditUserId: session?.uid || '',
    audit_user_email: session?.email || '',
    auditUserEmail: session?.email || '',
    source: 'portal-ci-finiquitos',
    request_kind: documentMode,
    session: {
      uid: session?.uid || '',
      email: session?.email || '',
      displayName: session?.displayName || ''
    }
  };
}

function buildDocumentPreviewRows(perceptions = [], deductions = []) {
  const maxLength = Math.max(perceptions.length, deductions.length);
  return Array.from({ length: maxLength }, (_, index) => ({
    perception: perceptions[index] || null,
    deduction: deductions[index] || null
  }));
}

function SectionHeader({ eyebrow, title, description, action }) {
  return (
    <div className="finiquitos-section-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h3>{title}</h3>
        {description && <p className="subtitle">{description}</p>}
      </div>
      {action}
    </div>
  );
}

function ConceptList({
  title,
  description,
  rows,
  presetOptions = [],
  selectedPresetValues = [],
  onAdd,
  onAddSelectedPresets,
  onRemove,
  onChange,
  onPresetSelectionChange,
  sectionLabel,
  showType = false
}) {
  const hasPresetOptions = presetOptions.length > 0;
  const selectedPresetValue = Array.isArray(selectedPresetValues)
    ? selectedPresetValues[0] || ''
    : selectedPresetValues || '';

  return (
    <article className="finiquitos-subpanel">
      <SectionHeader
        eyebrow="Conceptos"
        title={title}
        description={description}
        action={
          <button type="button" className="btn ghost compact" onClick={onAdd}>
            +
          </button>
        }
      />
      {hasPresetOptions && (
        <div className="finiquitos-concept-picker" aria-label={`${title}: catálogo de conceptos`}>
          <div className="finiquitos-concept-picker-head">
            <div>
              <span>Catálogo sugerido</span>
              <p>Selecciona un concepto y agrégalo como fila editable.</p>
            </div>
          </div>
          <div className="finiquitos-concept-select-row">
            <label className="filter-control compact-field grow">
              <span>Concepto sugerido</span>
              <select value={selectedPresetValue} onChange={event => onPresetSelectionChange(event.target.value)}>
                <option value="">Selecciona</option>
                {presetOptions.map(option => (
                  <option key={`${sectionLabel}-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn ghost compact"
              onClick={onAddSelectedPresets}
              disabled={!selectedPresetValue}
            >
              Agregar concepto
            </button>
          </div>
        </div>
      )}
      <div className="finiquitos-row-list">
        {rows.length ? (
          rows.map((row, index) => (
            <div className={`finiquitos-row ${showType ? 'with-type' : ''}`} key={row.id}>
              {showType && (
                <label className="filter-control compact-field">
                  <span>Tipo</span>
                  <select
                    value={row.type}
                    onChange={event => onChange(row.id, 'type', event.target.value)}
                  >
                    <option value="">Selecciona</option>
                    {FINIQUITOS_BENEFIT_TYPE_OPTIONS.map(option => (
                      <option key={`${row.id}-${option.value}`} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="filter-control compact-field grow">
                <span>Concepto</span>
                <input
                  type="text"
                  value={row.label}
                  onChange={event => onChange(row.id, 'label', event.target.value)}
                  placeholder="Ej. bono, apoyo, finiquito adicional"
                />
              </label>
              <label className="filter-control compact-field amount-field">
                <span>Monto</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={row.amount}
                  onChange={event => onChange(row.id, 'amount', event.target.value)}
                  placeholder="0.00"
                />
              </label>
              <label className="filter-control compact-field grow note-field">
                <span>Nota</span>
                <input
                  type="text"
                  value={row.note}
                  onChange={event => onChange(row.id, 'note', event.target.value)}
                  placeholder="Opcional"
                />
              </label>
              <button
                type="button"
                className="btn ghost compact remove-row-btn"
                onClick={() => onRemove(row.id)}
                aria-label={`Eliminar ${sectionLabel} ${index + 1}`}
              >
                ×
              </button>
            </div>
          ))
        ) : (
          <p className="helper-text">Aún no agregas conceptos.</p>
        )}
      </div>
    </article>
  );
}

function FiniquitosView({ session }) {
  const sessionLabel = session?.email || session?.displayName || 'sesión activa';
  const apiConfigError = getFiniquitosApiConfigError();
  const [employerId, setEmployerId] = useState('');
  const [employee, setEmployee] = useState(FINIQUITOS_DEFAULT_EMPLOYEE);
  const [separation, setSeparation] = useState(() => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      ...FINIQUITOS_DEFAULT_SEPARATION,
      fechaCalculo: today,
      fechaFirma: today
    };
  });
  const [vacationSheet, setVacationSheet] = useState(() => createVacationSpreadsheetData(DEFAULT_VACATION_TABLE));
  const [showVacationTable, setShowVacationTable] = useState(false);
  const [perceptions, setPerceptions] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [selectedConceptPresetValues, setSelectedConceptPresetValues] = useState({
    perceptions: '',
    deductions: '',
    benefits: ''
  });
  const [employerSearchText, setEmployerSearchText] = useState('');
  const [employerSearchField, setEmployerSearchField] = useState('all');
  const [approvalConfirmed, setApprovalConfirmed] = useState(false);
  const [renunciaVoluntariaConfirmed, setRenunciaVoluntariaConfirmed] = useState(false);
  const [backendStatus, setBackendStatus] = useState(() => ({
    status: apiConfigError ? 'unconfigured' : 'checking',
    message: apiConfigError || 'Verificando backend DOCX...',
    code: apiConfigError ? 'FINIQUITOS_API_URL_MISSING' : '',
    details: [],
    ready: false
  }));
  const [submitState, setSubmitState] = useState({
    status: 'pending',
    message: ''
  });
  const [generationResult, setGenerationResult] = useState(null);
  const [activeDownloadId, setActiveDownloadId] = useState('');
  const [calculationRunId] = useState(() => buildRunId());

  const { employers, loadingEmployers, employersError } = useFiniquitosEmployers({
    enabled: true
  });

  useEffect(() => {
    if (!employerId && employers.length === 1) {
      setEmployerId(employers[0].id);
    }
  }, [employerId, employers]);

  useEffect(() => {
    if (!isRenunciaVoluntaria(separation.motivo) && renunciaVoluntariaConfirmed) {
      setRenunciaVoluntariaConfirmed(false);
    }
  }, [renunciaVoluntariaConfirmed, separation.motivo]);

  useEffect(() => {
    let cancelled = false;

    if (apiConfigError) {
      setBackendStatus({
        status: 'unconfigured',
        message: apiConfigError,
        code: 'FINIQUITOS_API_URL_MISSING',
        details: [],
        ready: false
      });
      return () => {
        cancelled = true;
      };
    }

    setBackendStatus({
      status: 'checking',
      message: 'Verificando backend DOCX...',
      code: '',
      details: [],
      ready: false
    });

    checkFiniquitosBackendHealth()
      .then(result => {
        if (!cancelled) {
          setBackendStatus({
            status: result?.status || 'unavailable',
            message: result?.message || '',
            code: result?.code || '',
            details: Array.isArray(result?.details) ? result.details : [],
            ready: Boolean(result?.ready)
          });
        }
      })
      .catch(error => {
        if (!cancelled) {
          setBackendStatus({
            status: 'unavailable',
            message: error?.message || 'No fue posible consultar el health del backend DOCX.',
            code: error?.code || 'FINIQUITOS_HEALTH_UNAVAILABLE',
            details: Array.isArray(error?.details) ? error.details : [],
            ready: false
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiConfigError]);

  const markDraftChanged = () => {
    setApprovalConfirmed(false);
    setSubmitState({
      status: 'pending',
      message: ''
    });
    setGenerationResult(null);
    setActiveDownloadId('');
  };

  const updateVacationSheet = nextSheet => {
    markDraftChanged();
    setVacationSheet(nextSheet);
    setSeparation(previous => {
      if (previous.vacationTableMode !== FINIQUITOS_VACATION_TABLE_MODES.legal) {
        return previous;
      }
      return {
        ...previous,
        vacationTableMode: FINIQUITOS_VACATION_TABLE_MODES.contractualManual
      };
    });
  };

  const selectedEmployer = useMemo(
    () => employers.find(item => item.id === employerId) || null,
    [employers, employerId]
  );

  const filteredEmployers = useMemo(() => {
    const query = employerSearchText.trim().toLowerCase();
    if (!query) return employers;

    return employers.filter(employer => {
      const haystack =
        employerSearchField === 'label'
          ? employer.label || ''
          : employerSearchField === 'rfc'
            ? employer.rfc || ''
            : employerSearchField === 'patronal'
              ? employer.patronal || ''
              : employerSearchField === 'code'
                ? employer.code || ''
                : employer.searchValue || '';
      return haystack.toLowerCase().includes(query);
    });
  }, [employerSearchField, employerSearchText, employers]);

  const visibleEmployers = useMemo(() => {
    if (!selectedEmployer) return filteredEmployers;
    if (filteredEmployers.some(employer => employer.id === selectedEmployer.id)) {
      return filteredEmployers;
    }
    return [selectedEmployer, ...filteredEmployers];
  }, [filteredEmployers, selectedEmployer]);

  const validation = useMemo(() => {
    const errors = [];
    const warnings = [];

    const readRequiredText = (value, label) => {
      try {
        return normalizeDraftText(value, label, { required: true });
      } catch (error) {
        errors.push(error.message);
        return '';
      }
    };

    const readOptionalText = (value, label) => {
      try {
        return normalizeDraftText(value, label);
      } catch (error) {
        errors.push(error.message);
        return '';
      }
    };

    const readRequiredNumber = (value, label) => {
      try {
        return normalizeDraftNumber(value, label, { required: true });
      } catch (error) {
        errors.push(error.message);
        return null;
      }
    };

    const readOptionalNumber = (value, label) => {
      try {
        return normalizeDraftNumber(value, label);
      } catch (error) {
        errors.push(error.message);
        return null;
      }
    };

    const normalizedEmployee = {
      nombre: readRequiredText(employee.nombre, 'Nombre del empleado'),
      puesto: readOptionalText(employee.puesto, 'Puesto'),
      rfc: readOptionalText(employee.rfc, 'RFC del empleado'),
      curp: readRequiredText(employee.curp, 'CURP del empleado'),
      nss: readOptionalText(employee.nss, 'NSS del empleado'),
      correo: readOptionalText(employee.correo, 'Correo del empleado'),
      telefono: readOptionalText(employee.telefono, 'Teléfono del empleado')
    };

    const fechaIngreso = readRequiredText(separation.fechaIngreso, 'Fecha de ingreso');
    const normalizedSeparation = {
      motivo: readRequiredText(separation.motivo, 'Motivo de separación'),
      fechaCalculo: readOptionalText(separation.fechaCalculo, 'Fecha de cálculo'),
      fechaIngreso,
      fechaAlta: fechaIngreso,
      fechaSeparacion: readRequiredText(separation.fechaSeparacion, 'Fecha de baja'),
      fechaFirma: readOptionalText(separation.fechaFirma, 'Fecha de firma'),
      ciudadFirma: readOptionalText(separation.ciudadFirma, 'Ciudad de firma'),
      estadoFirma: readOptionalText(separation.estadoFirma, 'Estado de firma'),
      salarioDiario: readRequiredNumber(separation.salarioDiario, 'Salario diario'),
      salarioSemanal: null,
      aguinaldoDiasAnuales:
        readOptionalNumber(separation.aguinaldoDiasAnuales, 'Días anuales de aguinaldo') || 15,
      aguinaldoDiasLaborados: readOptionalNumber(
        separation.aguinaldoDiasLaborados,
        'Días laborados para aguinaldo'
      ),
      diasIndemnizacion: readOptionalNumber(separation.diasIndemnizacion, 'Días de indemnización') || 0,
      vacacionesNoPagadasDias:
        readOptionalNumber(separation.vacacionesNoPagadasDias, 'Días de vacaciones no pagadas') || 0,
      testigo1Nombre: readOptionalText(separation.testigo1Nombre, 'Nombre del testigo 1'),
      testigo2Nombre: readOptionalText(separation.testigo2Nombre, 'Nombre del testigo 2'),
      vacationTableMode: normalizeVacationTableMode(separation.vacationTableMode),
      vacationTableOverrideReason: readOptionalText(
        separation.vacationTableOverrideReason,
        'Justificación del override manual'
      )
    };
    normalizedSeparation.fechaAlta = normalizedSeparation.fechaIngreso;
    normalizedSeparation.fechaFirma = normalizedSeparation.fechaFirma || normalizedSeparation.fechaSeparacion;
    normalizedSeparation.fechaCalculo = normalizedSeparation.fechaCalculo || new Date().toISOString().slice(0, 10);
    normalizedSeparation.salarioSemanal = Number((normalizedSeparation.salarioDiario * 7).toFixed(2));

    if (!selectedEmployer) {
      errors.push('Selecciona un empleador desde Airtable.');
    }

    const renunciaVoluntaria = isRenunciaVoluntaria(normalizedSeparation.motivo);

    const vacationRowsInput = spreadsheetDataToVacationRows(vacationSheet);
    const vacationValidation = validateVacationRows(vacationRowsInput);
    errors.push(...vacationValidation.errors);
    if (
      normalizedSeparation.vacationTableMode === FINIQUITOS_VACATION_TABLE_MODES.manualOverride &&
      !normalizedSeparation.vacationTableOverrideReason
    ) {
      errors.push('La justificación del override manual es obligatoria para la tabla vacacional excepcional.');
    }
    if (
      normalizedSeparation.vacationTableMode === FINIQUITOS_VACATION_TABLE_MODES.legal &&
      JSON.stringify(vacationValidation.normalizedRows) !== JSON.stringify(createVacationSpreadsheetData(DEFAULT_VACATION_TABLE).map(row => ({
        yearsWorked: Number(row[0].value),
        vacationDays: Number(row[1].value)
      })))
    ) {
      warnings.push('La tabla vacacional dejó de coincidir con el catálogo legal y se tratará como contractual/manual.');
    }

    const perceptionsValidation = normalizeConceptRows(perceptions, {
      sectionLabel: 'percepciones',
      category: 'percepcion',
      direction: 'credit'
    });
    const deductionsValidation = normalizeConceptRows(deductions, {
      sectionLabel: 'deducciones',
      category: 'deduccion',
      direction: 'debit'
    });
    const benefitsValidation = normalizeConceptRows(benefits, {
      sectionLabel: 'prestaciones',
      direction: 'credit',
      requiresType: true
    });

    errors.push(...perceptionsValidation.errors);
    errors.push(...deductionsValidation.errors);
    errors.push(...benefitsValidation.errors);

    const hasAnyManualConcept =
      perceptionsValidation.normalizedRows.length ||
      deductionsValidation.normalizedRows.length ||
      benefitsValidation.normalizedRows.length;

    if (!hasAnyManualConcept) {
      warnings.push('No capturaste conceptos manuales; el cálculo usará solo las prestaciones legales.');
    }

    if (renunciaVoluntaria) {
      warnings.push('La renuncia solo se generará con la misma corrida aprobada si confirmas expresamente este motivo.');
    } else if (normalizedSeparation.motivo) {
      warnings.push('La renuncia solo aplica para renuncia voluntaria confirmada; esta corrida generará solo finiquito.');
    }

    if (employersError) {
      warnings.push(employersError);
    }

    if (loadingEmployers) {
      warnings.push('Cargando catálogo de empleadores desde Airtable.');
    }

    const employerPayload = makeEmployerPayload(selectedEmployer);
    const employeePayload = normalizedEmployee;
    const allConcepts = [
      ...perceptionsValidation.normalizedRows,
      ...deductionsValidation.normalizedRows,
      ...benefitsValidation.normalizedRows
    ];
    const manualIsrAmount = deductionsValidation.normalizedRows
      .filter(isManualIsrConcept)
      .reduce((sum, concept) => sum + Number(concept.amount || 0), 0);
    const concepts = allConcepts.filter(concept => !isManualIsrConcept(concept));

    let calculation = null;

    if (!errors.length) {
      try {
        calculation = calculateFiniquito({
          startDate: normalizedSeparation.fechaIngreso,
          endDate: normalizedSeparation.fechaSeparacion,
          dailySalary: normalizedSeparation.salarioDiario,
          aguinaldoDays: normalizedSeparation.aguinaldoDiasAnuales,
          aguinaldoDaysWorkedOverride: normalizedSeparation.aguinaldoDiasLaborados,
          indemnizacionDays: normalizedSeparation.diasIndemnizacion,
          manualIsrAmount,
          unpaidVacationDays: normalizedSeparation.vacacionesNoPagadasDias,
          vacationTable: vacationValidation.normalizedRows,
          vacationTableVersion: DEFAULT_VACATION_TABLE_VERSION,
          concepts,
          employee: employeePayload,
          employer: employerPayload || {}
        });
      } catch (error) {
        errors.push(error.message);
      }
    }

    if (calculation) {
      if (calculation.totals.deductionsTotal > calculation.totals.grossTotal) {
        errors.push('Las deducciones no pueden ser mayores que el bruto calculado.');
        calculation = null;
      } else if (calculation.totals.deductionsTotal === calculation.totals.grossTotal && calculation.totals.grossTotal > 0) {
        warnings.push('El neto quedará en cero porque las deducciones igualan el bruto.');
      }
    }

    if (apiConfigError) {
      warnings.push(apiConfigError);
    }

    if (!selectedEmployer && employers.length > 0) {
      warnings.push('Todavía no seleccionas un empleador.');
    }

    const documentRequests = {
      finiquito: true,
      renuncia: renunciaVoluntaria
    };
    const documentMode = renunciaVoluntaria ? 'bundle' : 'finiquito';
    const vacationConfig = buildVacationConfigSnapshot({
      mode: normalizedSeparation.vacationTableMode,
      overrideReason: normalizedSeparation.vacationTableOverrideReason,
      allowManualOverride: normalizedSeparation.vacationTableMode === FINIQUITOS_VACATION_TABLE_MODES.manualOverride,
      rows: vacationValidation.normalizedRows
    });
    const documentSnapshot = calculation
      ? buildFiniquitosDocumentSnapshot({
          calculation,
          employer: employerPayload,
          employee: employeePayload,
          dates: {
            fechaCalculo: normalizedSeparation.fechaCalculo,
            fechaIngreso: normalizedSeparation.fechaIngreso,
            fechaAlta: normalizedSeparation.fechaAlta,
            fechaBaja: normalizedSeparation.fechaSeparacion,
            fechaFirma: normalizedSeparation.fechaFirma,
            ciudadFirma: normalizedSeparation.ciudadFirma,
            estadoFirma: normalizedSeparation.estadoFirma
          },
          salary: {
            sueldoSemanal: normalizedSeparation.salarioSemanal,
            sueldoDiario: normalizedSeparation.salarioDiario
          },
          benefits: {
            aguinaldoDiasAnuales: normalizedSeparation.aguinaldoDiasAnuales,
            aguinaldoDiasLaborados: normalizedSeparation.aguinaldoDiasLaborados,
            diasIndemnizacion: normalizedSeparation.diasIndemnizacion,
            vacacionesNoPagadasDias: normalizedSeparation.vacacionesNoPagadasDias
          },
          vacationConfig,
          renuncia: {
            aplica: renunciaVoluntaria,
            motivoBaja: normalizedSeparation.motivo
          },
          signatures: {
            employeeName: normalizedEmployee.nombre,
            employeeCurp: normalizedEmployee.curp,
            testigo1Nombre: normalizedSeparation.testigo1Nombre,
            testigo2Nombre: normalizedSeparation.testigo2Nombre
          }
        })
      : null;

    if (documentSnapshot) {
      const missingDocumentFields = getMissingDocumentFields(documentSnapshot);
      if (missingDocumentFields.length > 0) {
        errors.push(
          `Faltan datos obligatorios para generar el DOCX: ${missingDocumentFields.join(', ')}.`
        );
      }
    }

    return {
      errors,
      warnings,
      calculation,
      employee: employeePayload,
      employer: employerPayload,
      separation: normalizedSeparation,
      vacationConfig,
      documentSnapshot,
      concepts,
      manualIsrAmount,
      capturedConceptCount: allConcepts.length,
      vacationRows: vacationValidation.normalizedRows,
      documentRequests,
      documentMode,
      renunciaVoluntaria
    };
  }, [
    apiConfigError,
    deductions,
    employee,
    employers.length,
    employersError,
    benefits,
    loadingEmployers,
    perceptions,
    renunciaVoluntariaConfirmed,
    selectedEmployer,
    separation,
    vacationSheet
  ]);

  const showBlockingError = validation.errors.length > 0;
  const backendIsReady = backendStatus.status === 'ready';
  const approvalEnabled = Boolean(
    approvalConfirmed &&
      validation.calculation &&
      !showBlockingError &&
      backendIsReady &&
      !loadingEmployers &&
      selectedEmployer &&
      (!validation.renunciaVoluntaria || renunciaVoluntariaConfirmed)
  );

  const handleEmployeeChange = field => event => {
    const { value } = event.target;
    markDraftChanged();
    setEmployee(previous => ({ ...previous, [field]: value }));
  };

  const handleSeparationChange = field => event => {
    const { value } = event.target;
    markDraftChanged();
    setSeparation(previous => ({ ...previous, [field]: value }));
  };

  const handleConceptChange = (setter, id, field, value) => {
    markDraftChanged();
    setter(previous =>
      previous.map(row => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleAddConcept = setter => {
    markDraftChanged();
    setter(previous => [...previous, createEmptyConceptRow()]);
  };

  const handleAddBenefit = () => {
    markDraftChanged();
    setBenefits(previous => [...previous, createEmptyBenefitRow()]);
  };

  const handleConceptPresetSelectionChange = (section, values) => {
    setSelectedConceptPresetValues(previous => ({
      ...previous,
      [section]: values
    }));
  };

  const handleAddSelectedConceptPresets = (section, setter, createRow) => {
    const selectedValue = Array.isArray(selectedConceptPresetValues[section])
      ? selectedConceptPresetValues[section][0] || ''
      : selectedConceptPresetValues[section] || '';
    if (!selectedValue) return;

    const selectedPreset = (FINIQUITOS_CONCEPT_PRESETS[section] || []).find(option => option.value === selectedValue);
    if (!selectedPreset) return;

    markDraftChanged();
    setter(previous => [...previous, createRow(selectedPreset)]);
    setSelectedConceptPresetValues(previous => ({
      ...previous,
      [section]: ''
    }));
  };

  const handleRemoveConcept = (setter, id) => {
    markDraftChanged();
    setter(previous => previous.filter(row => row.id !== id));
  };

  const handleToggleVacationTable = () => {
    setShowVacationTable(previous => !previous);
  };

  const handleResetVacationTable = () => {
    markDraftChanged();
    setVacationSheet(createVacationSpreadsheetData(DEFAULT_VACATION_TABLE));
    setSeparation(previous => ({
      ...previous,
      vacationTableMode: FINIQUITOS_VACATION_TABLE_MODES.legal,
      vacationTableOverrideReason: ''
    }));
  };

  const handleEmployerChange = event => {
    markDraftChanged();
    setEmployerId(event.target.value);
  };

  const handleApprovalChange = event => {
    const checked = event.target.checked;
    setApprovalConfirmed(checked);
    if (!checked) {
      setSubmitState({
        status: 'pending',
        message: ''
      });
      setGenerationResult(null);
      setActiveDownloadId('');
    }
  };

  const handleRenunciaConfirmationChange = event => {
    const checked = event.target.checked;
    setRenunciaVoluntariaConfirmed(checked);
    if (!checked) {
      setSubmitState({
        status: 'pending',
        message: ''
      });
      setGenerationResult(null);
      setActiveDownloadId('');
    }
  };

  const handleDownloadDocument = async fileDocument => {
    const downloadId = fileDocument?.id || fileDocument?.type || 'documento';
    if (!fileDocument?.downloadUrl) {
      setSubmitState({
        status: 'technical_error',
        message: 'El backend no devolvió una URL de descarga autenticada.'
      });
      return;
    }

    setActiveDownloadId(downloadId);
    try {
      const file = await downloadFiniquitosDocument({
        downloadUrl: fileDocument.downloadUrl,
        filename: fileDocument.fileName || `${fileDocument.type || 'documento'}.docx`
      });
      const blobUrl = window.URL.createObjectURL(file.blob);
      const anchor = window.document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = file.filename || fileDocument.fileName || `${fileDocument.type || 'documento'}.docx`;
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      setSubmitState({
        status: mapBackendErrorToSubmitStatus(error),
        message: error.message || 'No fue posible descargar el DOCX autenticado.'
      });
    } finally {
      setActiveDownloadId('');
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!approvalEnabled) {
      setSubmitState({
        status: backendStatus.status === 'unconfigured' ? 'backend_unconfigured' : 'technical_error',
        message:
          backendStatus.message ||
          'Corrige los errores, confirma la revisión y espera a que el backend esté listo antes de enviar el cálculo.'
      });
      return;
    }

    setSubmitState({
      status: 'generating',
      message: 'Generando documentos desde la corrida aprobada...'
    });
    setGenerationResult(null);

    try {
      const auditUser = {
        id: session?.uid || '',
        email: session?.email || '',
        displayName: session?.displayName || ''
      };
      const documentSnapshot = validation.documentSnapshot;
      const payload = {
        ...buildLegacyFiniquitosPayload({
          validation,
          session,
          calculationRunId,
          documentSnapshot,
          documentMode: validation.documentMode,
          documentRequests: validation.documentRequests
        }),
        template_version: FINIQUITOS_DOCUMENT_TEMPLATE_VERSION,
        templateVersion: FINIQUITOS_DOCUMENT_TEMPLATE_VERSION,
        manual_isr_amount: validation.manualIsrAmount,
        manualIsrAmount: validation.manualIsrAmount,
        vacationTable: validation.vacationRows,
        concepts: validation.concepts
      };

      const submitGeneration =
        validation.documentMode === 'bundle' ? submitFiniquitosBundle : submitFiniquitoDocument;
      const response = await submitGeneration({ payload });
      const responseRunId =
        response?.calculationRunId ||
        response?.calculation_run_id ||
        response?.id ||
        calculationRunId;
      const nextStatus = response?.status || 'generated';

      setGenerationResult(response);
      setSubmitState({
        status: nextStatus,
        message:
          response?.message ||
          (nextStatus === 'generated'
            ? `Documentos generados. Run ${responseRunId}.`
            : nextStatus === 'generating'
              ? `El backend sigue generando la corrida ${responseRunId}.`
              : `La corrida ${responseRunId} quedó pendiente en el backend.`)
      });
    } catch (error) {
      setSubmitState({
        status: mapBackendErrorToSubmitStatus(error),
        message: error.message || 'No fue posible generar los documentos en el backend.'
      });
    }
  };

  const preview = validation.calculation;
  const totals = preview?.totals || { grossTotal: 0, deductionsTotal: 0, netTotal: 0 };
  const lineItems = preview?.lineItems || [];
  const documentSnapshot = validation.documentSnapshot;
  const selectedEmployerRegistration =
    selectedEmployer?.registroPatronal || selectedEmployer?.patronal || '';
  const documentPreviewRows = buildDocumentPreviewRows(
    documentSnapshot?.percepciones || [],
    documentSnapshot?.deducciones || []
  );
  const employerChip = selectedEmployer
    ? `${selectedEmployer.company}${selectedEmployer.rfc ? ` · ${selectedEmployer.rfc}` : ''}${selectedEmployerRegistration ? ` · ${selectedEmployerRegistration}` : ''}`
    : 'Sin empleador';
  const conceptCount = validation.capturedConceptCount || perceptions.length || deductions.length || benefits.length;
  const showNeedAirtableHint = !loadingEmployers && !employers.length;
  const generationStatusMeta = getGenerationStatusMeta(submitState.status);
  const backendStatusMeta = getBackendStatusMeta(backendStatus.status);

  return (
    <section className="finiquitos-view" data-ui-view="finiquitos" data-ui-section="finiquitos-view">
      <div className="finiquitos-header">
        <div>
          <p className="eyebrow">Portal CI · Finiquitos</p>
          <h2>Captura y prepara el cálculo</h2>
          <p className="subtitle">
            La vista arma la solicitud, valida la captura y prepara el snapshot para el backend.
            El cálculo definitivo y la generación DOCX deben resolverse del lado del servidor.
          </p>
        </div>
        <div className="finiquitos-status-stack" aria-label="Estado del módulo">
          <span className={`status-badge ${backendStatusMeta.className}`}>
            {backendStatusMeta.label}
          </span>
          <span className="status-badge neutral">Run ID {calculationRunId}</span>
          <span className={`status-badge ${generationStatusMeta.className}`}>
            {generationStatusMeta.label}
          </span>
          <span className={`status-badge ${validation.renunciaVoluntaria ? 'warning' : 'ready'}`}>
            {validation.documentMode === 'bundle' ? 'DOCX finiquito + renuncia' : 'DOCX finiquito'}
          </span>
          <span className="status-badge neutral">Sesión {sessionLabel}</span>
        </div>
        {backendStatus.message && backendStatus.status !== 'ready' && (
          <p
            className={`helper-text ${
              backendStatus.status === 'unconfigured'
                ? 'warning'
                : backendStatus.status === 'unavailable' || backendStatus.status === 'unhealthy'
                  ? 'error'
                  : ''
            }`}
          >
            {backendStatus.message}
          </p>
        )}
      </div>

      <form className="finiquitos-layout" onSubmit={handleSubmit}>
        <div className="finiquitos-main">
          <article className="finiquitos-panel">
            <SectionHeader
              eyebrow="Empleador"
              title="Selecciona el patrón desde Airtable"
              description="El catálogo se carga desde Airtable, solo muestra clientes con ESTATUS ACTIVO y se puede filtrar por texto."
              action={
                <span className={`status-badge ${loadingEmployers ? 'warning' : employers.length ? 'ready' : 'neutral'}`}>
                  {loadingEmployers
                    ? 'Cargando'
                    : employers.length
                      ? `${formatCount(employers.length)} activos`
                      : 'Sin catálogo'}
                </span>
              }
            />
            <div className="finiquitos-panel-body">
              <div className="finiquitos-search-tools">
                <label className="filter-control">
                  <span>Buscar cliente</span>
                  <input
                    type="text"
                    value={employerSearchText}
                    onChange={event => setEmployerSearchText(event.target.value)}
                    placeholder="Empresa, RFC, ID cliente o registro patronal"
                  />
                </label>
                <label className="filter-control">
                  <span>Buscar por</span>
                  <select value={employerSearchField} onChange={event => setEmployerSearchField(event.target.value)}>
                    {EMPLOYER_SEARCH_FIELDS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="btn ghost compact finiquitos-clear-search"
                  onClick={() => {
                    setEmployerSearchText('');
                    setEmployerSearchField('all');
                  }}
                  disabled={!employerSearchText && employerSearchField === 'all'}
                >
                  Limpiar
                </button>
              </div>
              <div className="finiquitos-search-hint">
                <span className="data-pill">{formatCount(filteredEmployers.length)} resultados visibles</span>
                <span className="data-pill">Solo ESTATUS ACTIVO</span>
              </div>
              {!loadingEmployers && !filteredEmployers.length && (
                <p className="helper-text">
                  No hay clientes activos que coincidan con la búsqueda. Ajusta el texto o limpia los filtros.
                </p>
              )}
              <label className="filter-control finiquitos-select">
                <span>Empleador</span>
                <select value={employerId} onChange={handleEmployerChange}>
                  <option value="">Selecciona un empleador</option>
                  {visibleEmployers.map(employer => (
                    <option key={employer.id} value={employer.id}>
                      {employer.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="finiquitos-chip-row">
                <span className="data-pill">{employerChip}</span>
                {selectedEmployerRegistration && (
                  <span className="data-pill">Patronal {selectedEmployerRegistration}</span>
                )}
                {selectedEmployer?.code && <span className="data-pill">Código {selectedEmployer.code}</span>}
              </div>
              {showNeedAirtableHint && (
                <p className="helper-text">No se pudo cargar el catálogo de empleadores. Revisa Airtable o usa un entorno con conexión.</p>
              )}
              {employersError && <p className="helper-text error">{employersError}</p>}
            </div>
          </article>

          <article className="finiquitos-panel">
            <SectionHeader
              eyebrow="Empleado"
              title="Captura los datos manuales"
              description="Usa texto simple. No se aceptan fórmulas ni valores que empiecen con =."
            />
            <div className="finiquitos-panel-body finiquitos-grid-two">
              <label className="filter-control">
                <span>Nombre completo</span>
                <input type="text" value={employee.nombre} onChange={handleEmployeeChange('nombre')} />
              </label>
              <label className="filter-control">
                <span>Puesto</span>
                <input type="text" value={employee.puesto} onChange={handleEmployeeChange('puesto')} />
              </label>
              <label className="filter-control">
                <span>RFC</span>
                <input type="text" value={employee.rfc} onChange={handleEmployeeChange('rfc')} />
              </label>
              <label className="filter-control">
                <span>CURP</span>
                <input type="text" value={employee.curp} onChange={handleEmployeeChange('curp')} />
              </label>
              <label className="filter-control">
                <span>NSS</span>
                <input type="text" value={employee.nss} onChange={handleEmployeeChange('nss')} />
              </label>
              <label className="filter-control">
                <span>Correo</span>
                <input type="text" value={employee.correo} onChange={handleEmployeeChange('correo')} />
              </label>
              <label className="filter-control finiquitos-span-full">
                <span>Teléfono</span>
                <input type="text" value={employee.telefono} onChange={handleEmployeeChange('telefono')} />
              </label>
            </div>
          </article>

          <article className="finiquitos-panel">
            <SectionHeader
              eyebrow="Separación"
              title="Motivo, fechas y salario"
              description="La calculadora usa esta base para obtener antigüedad y prestaciones proporcionales."
            />
            <div className="finiquitos-panel-body finiquitos-grid-three">
              <label className="filter-control">
                <span>Motivo de separación</span>
                <select value={separation.motivo} onChange={handleSeparationChange('motivo')}>
                  {FINIQUITOS_REASON_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="filter-control">
                <span>Fecha de cálculo</span>
                <input type="date" value={separation.fechaCalculo} onChange={handleSeparationChange('fechaCalculo')} />
              </label>
              <label className="filter-control">
                <span>Fecha de ingreso / Fecha de alta</span>
                <input type="date" value={separation.fechaIngreso} onChange={handleSeparationChange('fechaIngreso')} />
              </label>
              <label className="filter-control">
                <span>Fecha de baja</span>
                <input type="date" value={separation.fechaSeparacion} onChange={handleSeparationChange('fechaSeparacion')} />
              </label>
              <label className="filter-control">
                <span>Fecha de firma</span>
                <input type="date" value={separation.fechaFirma} onChange={handleSeparationChange('fechaFirma')} />
              </label>
              <label className="filter-control">
                <span>Ciudad de firma</span>
                <input type="text" value={separation.ciudadFirma} onChange={handleSeparationChange('ciudadFirma')} placeholder="Ciudad" />
              </label>
              <label className="filter-control">
                <span>Estado de firma</span>
                <input type="text" value={separation.estadoFirma} onChange={handleSeparationChange('estadoFirma')} placeholder="Estado" />
              </label>
              <label className="filter-control finiquitos-span-full">
                <span>Salario diario</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={separation.salarioDiario}
                  onChange={handleSeparationChange('salarioDiario')}
                  placeholder="0.00"
                />
              </label>
              <label className="filter-control">
                <span>Días anuales de aguinaldo</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={separation.aguinaldoDiasAnuales}
                  onChange={handleSeparationChange('aguinaldoDiasAnuales')}
                  placeholder="15"
                />
              </label>
              <label className="filter-control">
                <span>Días laborados para aguinaldo</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={separation.aguinaldoDiasLaborados}
                  onChange={handleSeparationChange('aguinaldoDiasLaborados')}
                  placeholder="Auto"
                />
              </label>
              <label className="filter-control">
                <span>Días de indemnización</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={separation.diasIndemnizacion}
                  onChange={handleSeparationChange('diasIndemnizacion')}
                  placeholder="0"
                />
              </label>
              <div className="finiquitos-chip-row finiquitos-span-full">
                <span className="data-pill">
                  Sueldo semanal estimado {formatCurrency(Number(separation.salarioDiario || 0) * 7)}
                </span>
              </div>
            </div>
          </article>

          <article className="finiquitos-panel">
            <SectionHeader
              eyebrow="Vacaciones"
              title="Tabla editable de años y días"
              description="La tabla se valida por orden ascendente, sin duplicados y sin decrementos en los días."
              action={
                <div className="finiquitos-section-actions">
                  <button
                    type="button"
                    className="btn ghost compact"
                    onClick={handleToggleVacationTable}
                    aria-expanded={showVacationTable}
                    aria-controls="finiquitos-vacation-table"
                  >
                    {showVacationTable ? 'Ocultar tabla' : 'Mostrar tabla'}
                  </button>
                  <button type="button" className="btn ghost compact" onClick={handleResetVacationTable}>
                    Restaurar legal
                  </button>
                </div>
              }
            />
            <div className="finiquitos-panel-body">
              <div className="finiquitos-grid-two">
                <label className="filter-control">
                  <span>Modo de tabla</span>
                  <select
                    value={separation.vacationTableMode}
                    onChange={handleSeparationChange('vacationTableMode')}
                  >
                    {FINIQUITOS_VACATION_TABLE_MODE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="filter-control">
                  <span>Justificación del override</span>
                  <input
                    type="text"
                    value={separation.vacationTableOverrideReason}
                    onChange={handleSeparationChange('vacationTableOverrideReason')}
                    placeholder="Opcional, requerido en override manual"
                  />
                </label>
                <label className="filter-control">
                  <span>Días de vacaciones no pagadas</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={separation.vacacionesNoPagadasDias}
                    onChange={handleSeparationChange('vacacionesNoPagadasDias')}
                    placeholder="0"
                  />
                </label>
              </div>
              {showVacationTable ? (
                <div className="finiquitos-spreadsheet-wrap" id="finiquitos-vacation-table">
                  <Spreadsheet
                    data={vacationSheet}
                    onChange={updateVacationSheet}
                    columnLabels={['Año cumplido', 'Días de vacaciones']}
                  />
                </div>
              ) : (
                <div className="finiquitos-collapsed-panel" id="finiquitos-vacation-table">
                  <span className="status-badge neutral">
                    {formatCount(vacationSheet.length)} filas ocultas
                  </span>
                  <p className="helper-text">
                    La tabla permanece cargada en el cálculo. Muéstrala para revisar o editar los días.
                  </p>
                </div>
              )}
              <p className="helper-text">
                La tabla legal base contiene {formatCount(DEFAULT_VACATION_TABLE.length)} filas y se puede ajustar antes del envío.
              </p>
            </div>
          </article>

          <ConceptList
            title="Percepciones"
            description="Bonos, apoyos o pagos adicionales que aumentan el bruto."
            rows={perceptions}
            presetOptions={FINIQUITOS_CONCEPT_PRESETS.perceptions}
            selectedPresetValues={selectedConceptPresetValues.perceptions}
            onAdd={() => handleAddConcept(setPerceptions)}
            onAddSelectedPresets={() =>
              handleAddSelectedConceptPresets('perceptions', setPerceptions, createConceptRowFromPreset)
            }
            onRemove={id => handleRemoveConcept(setPerceptions, id)}
            onChange={(id, field, value) => handleConceptChange(setPerceptions, id, field, value)}
            onPresetSelectionChange={values => handleConceptPresetSelectionChange('perceptions', values)}
            sectionLabel="percepciones"
          />

          <ConceptList
            title="Deducciones"
            description="Retenciones, préstamos y cualquier importe que reduzca el neto."
            rows={deductions}
            presetOptions={FINIQUITOS_CONCEPT_PRESETS.deductions}
            selectedPresetValues={selectedConceptPresetValues.deductions}
            onAdd={() => handleAddConcept(setDeductions)}
            onAddSelectedPresets={() =>
              handleAddSelectedConceptPresets('deductions', setDeductions, createConceptRowFromPreset)
            }
            onRemove={id => handleRemoveConcept(setDeductions, id)}
            onChange={(id, field, value) => handleConceptChange(setDeductions, id, field, value)}
            onPresetSelectionChange={values => handleConceptPresetSelectionChange('deductions', values)}
            sectionLabel="deducciones"
          />

          <ConceptList
            title="Prestaciones superiores y excepcionales"
            description="Conceptos adicionales con tipo selectable para el snapshot de backend."
            rows={benefits}
            presetOptions={FINIQUITOS_CONCEPT_PRESETS.benefits}
            selectedPresetValues={selectedConceptPresetValues.benefits}
            onAdd={handleAddBenefit}
            onAddSelectedPresets={() =>
              handleAddSelectedConceptPresets('benefits', setBenefits, createBenefitRowFromPreset)
            }
            onRemove={id => handleRemoveConcept(setBenefits, id)}
            onChange={(id, field, value) => handleConceptChange(setBenefits, id, field, value)}
            onPresetSelectionChange={values => handleConceptPresetSelectionChange('benefits', values)}
            sectionLabel="prestaciones"
            showType
          />

          <article className="finiquitos-panel">
            <SectionHeader
              eyebrow="Renuncia"
              title="Testigos y validación"
              description="Los testigos son opcionales y solo se usan cuando la separación es renuncia voluntaria."
            />
            <div className="finiquitos-panel-body finiquitos-grid-two">
              <label className="filter-control">
                <span>Testigo 1</span>
                <input
                  type="text"
                  value={separation.testigo1Nombre}
                  onChange={handleSeparationChange('testigo1Nombre')}
                  placeholder="Opcional"
                />
              </label>
              <label className="filter-control">
                <span>Testigo 2</span>
                <input
                  type="text"
                  value={separation.testigo2Nombre}
                  onChange={handleSeparationChange('testigo2Nombre')}
                  placeholder="Opcional"
                />
              </label>
            </div>
          </article>
        </div>

        <aside className="finiquitos-side">
          <article className="finiquitos-panel finiquitos-summary-panel">
            <SectionHeader
              eyebrow="Resumen"
              title="Vista previa y advertencias"
              description="La vista previa es local. El backend recalculará el finiquito antes de cerrar el flujo."
            />

            <div className="finiquitos-line-items">
              <p className="finiquitos-section-label">Detalle del cálculo local</p>
              {lineItems.length ? (
                lineItems.map(item => (
                  <div className="finiquitos-line-item" key={item.id}>
                    <div>
                      <strong>{item.label}</strong>
                      <small>{item.category}</small>
                    </div>
                    <span className={item.direction === 'debit' ? 'debit' : 'credit'}>
                      {formatCurrency(item.amount)}
                    </span>
                  </div>
                ))
              ) : (
                <p className="helper-text">Completa los datos para ver la vista previa.</p>
              )}
            </div>

            {validation.errors.length > 0 && (
              <div className="finiquitos-alert danger" role="alert">
                <strong>Corrige antes de aprobar</strong>
                <ul>
                  {validation.errors.slice(0, 8).map(error => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {!validation.errors.length && validation.warnings.length > 0 && (
              <div className="finiquitos-alert warning">
                <strong>Advertencias</strong>
                <ul>
                  {validation.warnings.slice(0, 8).map(warning => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="finiquitos-summary-grid">
              <div className="finiquitos-summary-card">
                <span>Bruto</span>
                <strong>{formatCurrency(totals.grossTotal)}</strong>
              </div>
              <div className="finiquitos-summary-card">
                <span>Deducciones</span>
                <strong>{formatCurrency(totals.deductionsTotal)}</strong>
              </div>
              <div className="finiquitos-summary-card emphasized">
                <span>Neto estimado</span>
                <strong>{formatCurrency(totals.netTotal)}</strong>
              </div>
              <div className="finiquitos-summary-card">
                <span>Conceptos</span>
                <strong>{formatCount(conceptCount)}</strong>
              </div>
            </div>

            {preview && (
              <div className="finiquitos-insight-grid">
                <div>
                  <span>Antigüedad</span>
                  <strong>{preview.antiquity.fullYearsCompleted} años cumplidos</strong>
                </div>
                <div>
                  <span>Periodo legal</span>
                  <strong>
                    {displayIsoDate(preview.antiquity.serviceCycleStart)} → {displayIsoDate(preview.antiquity.nextServiceCycleStart)}
                  </strong>
                </div>
                <div>
                  <span>Vacaciones</span>
                  <strong>{formatCount(preview.vacation.vacationDays)} días</strong>
                </div>
                <div>
                  <span>Aguinaldo</span>
                  <strong>{formatCurrency(preview.aguinaldo.amount)}</strong>
                </div>
                <div>
                  <span>Prima vacacional</span>
                  <strong>{formatCurrency(preview.vacation.vacationPremiumAmount)}</strong>
                </div>
                <div>
                  <span>Run ID</span>
                  <strong>{calculationRunId}</strong>
                </div>
              </div>
            )}

            <div className="finiquitos-doc-summary">
              <div className="finiquitos-doc-summary-item">
                <span>Snapshot</span>
                <strong>Aprobado con calculation_run_id</strong>
              </div>
              <div className="finiquitos-doc-summary-item">
                <span>Salida documental</span>
                <strong>
                  {validation.documentMode === 'bundle'
                    ? 'Finiquito + renuncia en un mismo bundle'
                    : 'Solo finiquito'}
                </strong>
              </div>
              <div className="finiquitos-doc-summary-item">
                <span>Renuncia</span>
                <strong>
                  {validation.renunciaVoluntaria
                    ? renunciaVoluntariaConfirmed
                      ? 'Confirmada'
                      : 'Pendiente confirmación expresa'
                    : 'No aplica'}
                </strong>
              </div>
            </div>

            {documentSnapshot && (
              <div className="finiquitos-document-preview">
                <div className="finiquitos-document-meta">
                  <div>
                    <span>Empleador</span>
                    <strong>
                      {documentSnapshot.employer.name || 'Sin dato'}
                      {documentSnapshot.employer.rfc ? ` · ${documentSnapshot.employer.rfc}` : ''}
                    </strong>
                  </div>
                  <div>
                    <span>Registro patronal</span>
                    <strong>{documentSnapshot.employer.registroPatronal || 'Sin dato'}</strong>
                  </div>
                  <div>
                    <span>Empleado</span>
                    <strong>{documentSnapshot.employee.fullName || 'Sin dato'}</strong>
                  </div>
                  <div>
                    <span>CURP</span>
                    <strong>{documentSnapshot.employee.curp || 'Sin dato'}</strong>
                  </div>
                  <div>
                    <span>Fecha de cálculo</span>
                    <strong>{displayIsoDate(documentSnapshot.dates.fechaCalculo)}</strong>
                  </div>
                  <div>
                    <span>Fecha de ingreso / alta</span>
                    <strong>{displayIsoDate(documentSnapshot.dates.fechaIngreso)}</strong>
                  </div>
                  <div>
                    <span>Fecha de baja</span>
                    <strong>{displayIsoDate(documentSnapshot.dates.fechaBaja)}</strong>
                  </div>
                  <div>
                    <span>Fecha de firma</span>
                    <strong>{displayIsoDate(documentSnapshot.dates.fechaFirma)}</strong>
                  </div>
                  <div>
                    <span>Lugar de firma</span>
                    <strong>
                      {documentSnapshot.dates.ciudadFirma || 'Sin ciudad'}
                      {documentSnapshot.dates.estadoFirma ? `, ${documentSnapshot.dates.estadoFirma}` : ''}
                    </strong>
                  </div>
                  <div>
                    <span>Sueldo diario</span>
                    <strong>{formatCurrency(documentSnapshot.salary.sueldoDiario)}</strong>
                  </div>
                  <div>
                    <span>Sueldo semanal</span>
                    <strong>{formatCurrency(documentSnapshot.salary.sueldoSemanal)}</strong>
                  </div>
                </div>
                <p className="finiquitos-section-label">Tabla documental</p>
                <table className="finiquitos-document-table">
                  <thead>
                    <tr>
                      <th colSpan={2}>Percepciones</th>
                      <th colSpan={2}>Deducciones</th>
                    </tr>
                    <tr>
                      <th>Concepto</th>
                      <th className="amount-col">Importe</th>
                      <th>Concepto</th>
                      <th className="amount-col">Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentPreviewRows.map((row, index) => (
                      <tr key={`doc-row-${index}`}>
                        <td>
                          {row.perception ? (
                            <>
                              <strong>{row.perception.concepto || row.perception.clave || 'Sin concepto'}</strong>
                              {row.perception.clave && <small>{row.perception.clave}</small>}
                            </>
                          ) : (
                            <span className="placeholder">—</span>
                          )}
                        </td>
                        <td className="amount-col">
                          {row.perception ? formatCurrency(row.perception.importe) : '—'}
                        </td>
                        <td>
                          {row.deduction ? (
                            <>
                              <strong>{row.deduction.concepto || row.deduction.clave || 'Sin concepto'}</strong>
                              {row.deduction.clave && <small>{row.deduction.clave}</small>}
                            </>
                          ) : (
                            <span className="placeholder">—</span>
                          )}
                        </td>
                        <td className="amount-col">
                          {row.deduction ? formatCurrency(row.deduction.importe) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th>TOTAL PERCEPCIONES</th>
                      <th className="amount-col">{formatCurrency(documentSnapshot.totals.totalPercepciones)}</th>
                      <th>TOTAL DEDUCCIONES</th>
                      <th className="amount-col">{formatCurrency(documentSnapshot.totals.totalDeducciones)}</th>
                    </tr>
                  </tfoot>
                </table>
                <div className="finiquitos-neto-box">
                  <span>NETO A PAGAR</span>
                  <strong>{formatCurrency(documentSnapshot.totals.netoPagar)}</strong>
                  <small>{documentSnapshot.totals.netoPagarTexto}</small>
                </div>
              </div>
            )}

            {generationResult && (
              <div className="finiquitos-generation-block">
                <div className="finiquitos-generation-meta">
                  <div>
                    <span>Estado</span>
                    <strong>{generationStatusMeta.label}</strong>
                  </div>
                  <div>
                    <span>Run ID</span>
                    <strong>{generationResult.calculationRunId || calculationRunId}</strong>
                  </div>
                  <div>
                    <span>Versión de plantilla</span>
                    <strong>{generationResult.templateVersion || FINIQUITOS_DOCUMENT_TEMPLATE_VERSION}</strong>
                  </div>
                  <div>
                    <span>Hash SHA-256</span>
                    <strong>{generationResult.hashSha256 || 'Pendiente de backend'}</strong>
                  </div>
                  <div>
                    <span>Almacenamiento</span>
                    <strong>{formatDocumentStorage(generationResult.storage)}</strong>
                  </div>
                  <div>
                    <span>Advertencias</span>
                    <strong>
                      {generationResult.warnings?.length
                        ? `${generationResult.warnings.length} pendientes`
                        : 'Sin advertencias'}
                    </strong>
                  </div>
                </div>
                {generationResult.documents?.length > 0 && (
                  <div className="finiquitos-document-list">
                    {generationResult.documents.map(fileDocument => {
                      const isDownloading = activeDownloadId === fileDocument.id;
                      return (
                        <article className="finiquitos-document-card" key={fileDocument.id}>
                          <div className="finiquitos-document-card-head">
                            <div>
                              <p className="finiquitos-document-kind">{fileDocument.label}</p>
                              <h4>{fileDocument.fileName || fileDocument.type}</h4>
                            </div>
                            <span className={`status-badge ${getGenerationStatusMeta(fileDocument.status).className}`}>
                              {getGenerationStatusMeta(fileDocument.status).label}
                            </span>
                          </div>
                          <div className="finiquitos-document-card-grid">
                            <div>
                              <span>Hash</span>
                              <strong>{fileDocument.hashSha256 || 'Pendiente'}</strong>
                            </div>
                            <div>
                              <span>Almacenamiento</span>
                              <strong>{formatDocumentStorage(fileDocument)}</strong>
                            </div>
                          </div>
                          <div className="finiquitos-document-card-actions">
                            <button
                              type="button"
                              className="btn ghost compact"
                              onClick={() => handleDownloadDocument(fileDocument)}
                              disabled={!fileDocument.downloadUrl || isDownloading}
                            >
                              {isDownloading ? 'Descargando...' : 'Descargar DOCX'}
                            </button>
                            {!fileDocument.downloadUrl && (
                              <p className="helper-text warning">El backend no expuso una descarga autenticada.</p>
                            )}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="finiquitos-approval-box">
              {validation.renunciaVoluntaria && (
              <label className="approval-check secondary">
                <input
                  type="checkbox"
                  checked={renunciaVoluntariaConfirmed}
                  onChange={handleRenunciaConfirmationChange}
                />
                <span>
                  Confirmo expresamente que la separación es una renuncia voluntaria y autorizo generar
                  el DOCX de renuncia con la misma corrida aprobada.
                </span>
                </label>
              )}
              <label className="approval-check">
                <input
                  type="checkbox"
                  checked={approvalConfirmed}
                  onChange={handleApprovalChange}
                />
                <span>Revisé la información y autorizo enviar el snapshot al backend.</span>
              </label>
              <button
                type="submit"
                className="btn primary full-width"
                disabled={!approvalEnabled || submitState.status === 'generating'}
              >
                {submitState.status === 'generating' ? 'Generando...' : 'Generar documentos'}
              </button>
              {submitState.message && (
                <p
                  className={`helper-text ${
                    submitState.status === 'business_error'
                      ? 'warning'
                      : submitState.status === 'backend_unconfigured'
                        ? 'warning'
                        : submitState.status === 'technical_error' || submitState.status === 'error'
                      ? 'error'
                      : submitState.status === 'generated'
                        ? 'success'
                        : submitState.status === 'generating'
                          ? 'warning'
                          : ''
                  }`}
                >
                  {submitState.message}
                </p>
              )}
              {validation.renunciaVoluntaria && !renunciaVoluntariaConfirmed && (
                <p className="helper-text warning">
                  La renuncia voluntaria queda bloqueada hasta confirmar expresamente que la misma corrida
                  generará el DOCX de renuncia.
                </p>
              )}
              {backendStatus.status !== 'ready' && backendStatus.status !== 'checking' && (
                <p className={`helper-text ${isBlockingBackendStatus(backendStatus.status) ? 'error' : 'warning'}`}>
                  {backendStatus.status === 'unconfigured'
                    ? 'El backend DOCX no está configurado todavía.'
                    : backendStatus.status === 'unhealthy'
                      ? 'El health del backend respondió como fallido.'
                      : 'El backend DOCX no está disponible en este momento.'}
                </p>
              )}
              <p className="helper-text">
                El backend debe regresar el cálculo definitivo y, si aplica, el bundle DOCX generado desde
                el mismo snapshot aprobado.
              </p>
            </div>

          </article>
        </aside>
      </form>
    </section>
  );
}

export default FiniquitosView;
